import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { money, moneyK, mesLabel } from './ui';

// Cada categoría con su color; el tono claro es lo que todavía no se cobró
export const CATEGORIAS = [
  { key: 'abono', label: 'Abonos', color: '#2E7DE2' },
  { key: 'desarrollo', label: 'Desarrollos', color: '#a855f7' },
  { key: 'hosting', label: 'Hosting', color: '#14b8a6' },
  { key: 'otros', label: 'Otros', color: '#94a3b8' },
];
const claro = (hex) => hex + '66';   // mismo color al 40%

/* Arma { mes: { categoria: { cobrado, pendiente, a_pasar } } } a partir de las filas del backend */
export function armarSerie(filas) {
  const meses = new Map();
  (filas || []).forEach(f => {
    const m = meses.get(f.mes) || { mes: mesLabel(f.mes), mesRaw: f.mes, total: 0, cobrado: 0, pendiente: 0, a_pasar: 0 };
    const cat = CATEGORIAS.some(c => c.key === f.categoria) ? f.categoria : 'otros';
    m[`${cat}_cobrado`] = (m[`${cat}_cobrado`] || 0) + Number(f.cobrado || 0);
    m[`${cat}_pendiente`] = (m[`${cat}_pendiente`] || 0) + Number(f.pendiente || 0);
    m.cobrado += Number(f.cobrado || 0);
    m.pendiente += Number(f.pendiente || 0);
    m.a_pasar += Number(f.a_pasar || 0);
    m.total += Number(f.cobrado || 0) + Number(f.pendiente || 0);
    meses.set(f.mes, m);
  });
  return [...meses.values()].sort((a, b) => a.mesRaw.localeCompare(b.mesRaw));
}

const tooltipStyle = { background: 'var(--tooltip-bg)', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'Raleway', color: 'var(--text-primary)' };

function TooltipSerie({ active, payload, label, unificado, fmt = money }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload || {};
  return (
    <div style={{ ...tooltipStyle, padding: '10px 12px', fontSize: 13, maxWidth: 'min(190px, 68vw)' }}>
      <div style={{ fontWeight: 700, marginBottom: 6, textTransform: 'capitalize' }}>{label}</div>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Facturado: {fmt(d.total)}</div>
      {unificado ? (
        <>
          <div style={{ color: '#22c55e' }}>Cobrado: {fmt(d.cobrado)}</div>
          {d.pendiente > 0 && <div style={{ color: '#f59e0b' }}>Falta cobrar: {fmt(d.pendiente)}</div>}
        </>
      ) : CATEGORIAS.map(c => {
        const co = d[`${c.key}_cobrado`] || 0;
        const pe = d[`${c.key}_pendiente`] || 0;
        if (!co && !pe) return null;
        return (
          <div key={c.key} style={{ marginBottom: 3 }}>
            <span style={{ color: c.color, fontWeight: 600 }}>{c.label}: </span>
            {fmt(co + pe)}
            {pe > 0 && <span style={{ color: 'var(--text-muted)' }}> · {fmt(pe)} sin cobrar</span>}
          </div>
        );
      })}
    </div>
  );
}

/* Facturación mes a mes: una columna por categoría, apilando cobrado y pendiente */
export default function SerieFacturacion({ datos, alto = 300, onElegir, extra, fmt = money, fmtCorto = moneyK }) {
  const [unificado, setUnificado] = useState(false);
  const usadas = CATEGORIAS.filter(c => datos.some(d => (d[`${c.key}_cobrado`] || 0) + (d[`${c.key}_pendiente`] || 0) > 0));
  const chip = (a) => ({
    padding: '4px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: '1px solid ' + (a ? 'var(--accent)' : 'var(--border)'),
    background: a ? 'var(--accent)' : 'transparent', color: a ? '#fff' : 'var(--text-secondary)',
  });
  return (
    <div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10, fontSize: 12, color: 'var(--text-secondary)', alignItems: 'center' }}>
        {unificado ? (
          <>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: '#22c55e' }} /> Cobrado
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 11, height: 11, borderRadius: 3, background: '#f59e0b' }} /> Falta cobrar
            </span>
          </>
        ) : usadas.map(c => (
          <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 11, height: 11, borderRadius: 3, background: c.color }} />
            <span style={{ width: 11, height: 11, borderRadius: 3, background: claro(c.color), marginLeft: -3 }} />
            {c.label}
          </span>
        ))}
        {!unificado && <span style={{ color: 'var(--text-muted)' }}>· el tono claro es lo que falta cobrar</span>}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <div style={chip(!unificado)} onClick={() => setUnificado(false)}>Por categoría</div>
          <div style={chip(unificado)} onClick={() => setUnificado(true)}>Unificado</div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={alto}>
        <BarChart data={datos} margin={{ left: -6 }} barGap={2} barCategoryGap="18%">
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis dataKey="mes" tick={{ fill: 'var(--chart-text)', fontSize: 11 }} />
          <YAxis tick={{ fill: 'var(--chart-text)', fontSize: 11 }} tickFormatter={fmtCorto} width={78} />
          <Tooltip content={<TooltipSerie unificado={unificado} fmt={fmt} />} cursor={{ fill: 'var(--accent-glow)', opacity: 0.35 }} />
          {unificado ? [
            <Bar key="u-c" dataKey="cobrado" stackId="total" fill="#22c55e" name="Cobrado"
              cursor={onElegir ? 'pointer' : undefined}
              onClick={(b) => onElegir && onElegir('pagado', b && b.payload && b.payload.mesRaw)} />,
            <Bar key="u-p" dataKey="pendiente" stackId="total" fill="#f59e0b" name="Falta cobrar" radius={[5, 5, 0, 0]}
              cursor={onElegir ? 'pointer' : undefined}
              onClick={(b) => onElegir && onElegir('abiertos', b && b.payload && b.payload.mesRaw)} />,
          ] : usadas.map(c => ([
            <Bar key={c.key + '-c'} dataKey={`${c.key}_cobrado`} stackId={c.key} fill={c.color} name={c.label}
              cursor={onElegir ? 'pointer' : undefined}
              onClick={(b) => onElegir && onElegir('pagado', b && b.payload && b.payload.mesRaw)} />,
            <Bar key={c.key + '-p'} dataKey={`${c.key}_pendiente`} stackId={c.key} fill={claro(c.color)} name={c.label + ' sin cobrar'}
              radius={[5, 5, 0, 0]} cursor={onElegir ? 'pointer' : undefined}
              onClick={(b) => onElegir && onElegir('abiertos', b && b.payload && b.payload.mesRaw)} />,
          ])).flat()}
          {extra}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
